import { File } from './file';
import {SharedDataService} from '../services/shared-data.service';
import {JobsListItem} from "./jobs";
import {User} from "./user";
import {ListItem} from "./listItem";

// TODO: Refactor to separate classes Files, Snapshorts, Backups which extend ListingModel and move logic to that classes
export class ListingModel {

  public get files(): File[] {
    if (!this.filterValue) {
      return this._files;
    }
    this.resetPositions()
    const filterValue = this.filterValue.toLowerCase();
    // Filtering files relative to value in filter input
    return this._files.filter(file => file.name.toLowerCase().includes(filterValue))
  };

  public set files(nFiles: File[]) {
    this.resetPositions()
    this._files = [...nFiles]; // !Important, leave it for virtual scroll re-render, because often stuck
    if (this._files.length) {
      this.sortFiles(this.snapshotsRoot ? SharedDataService.SORT_DIRECTIONS.NAME_DESC : this.savedSortRule);
    }
  };

  public get listItems(): ListItem[] {
    if (!this.filterValue) {
      return this._listItems;
    }
    this.resetPositions()
    const filterValue = this.filterValue.toLowerCase();
    // Filtering files relative to value in filter input
    return this._listItems.filter(item => item.filter_string.toLowerCase().includes(filterValue))
  };

  public set listItems(nItems: ListItem[]) {
    this.resetPositions()
    this._listItems = [...nItems]; // !Important, leave it for virtual scroll re-render, because often stuck
    if (this._listItems.length) {
      this.sortItems(this.savedSortRule);
    }
  };

  public get filterValue(): string {
    return this._filterValue;
  };

  public set filterValue(value: string) {
    this._filterValue = value;
  };

  public get selectedFiles(): File[] {
    return this._files.length ? this.files.filter(file => file.isSelected) : []
  }

  // Return active file selected with cursor or arrows
  public get lastSelectedFile(): File {
    return this.files[this.lastFocusedFile]
  }

  public get selectedListItems(): ListItem[] {
    return this._listItems.length ? this.listItems.filter(item => item.isSelected) : []
  }

  // Return active file selected with cursor or arrows
  public get lastSelectedListItem(): File {
    return this.listItems[this.lastFocusedFile]
  }

  static readonly SORT_DIRECTIONS = {
    name_asc: { field: 'name', direction: 1},
    id_asc: { field: 'id', direction: 1},
    size_asc: { field: 'size', direction: 1},
    date_asc: { field: 'cdate', direction: 1},
    points_asc: { field: 'points', direction: 1},
    name_desc: { field: 'name', direction: -1},
    id_desc: { field: 'id', direction: -1},
    size_desc: { field: 'size', direction: -1},
    date_desc: { field: 'cdate', direction: -1},
    points_desc: { field: 'points', direction: -1},
  };
  private _files: File[];
  private _listItems: ListItem[] = [];
  private _filterValue = '';
  private savedSortRule: string;

  public copyBuffer: Array<File> = [];
  public moveBuffer: Array<File> = [];
  public restoreBuffer: Array<File> = [];
  public lastFocusedFile = -1;
  public snapshotsRoot = false;

  public resetPositions() {
    this.lastFocusedFile = -1;
  }

  public clearSelection(): void {
    this.files.forEach(file => {
      file.isSelected = false;
      file.isInFocus = false;
    })
    this.lastFocusedFile = -1;
  }

  public clearBuffers() {
    this.clearCopyBuffer();
    this.clearMoveBuffer();
    this.clearRestoreBuffer();
  }

  public clearCopyBuffer() {
    this.copyBuffer.length = 0;
    window.sessionStorage.removeItem('copyBuffer')
  }
  public clearMoveBuffer() {
    this.moveBuffer.length = 0;
    window.sessionStorage.removeItem('moveBuffer')
  }
  public clearRestoreBuffer() {
    this.restoreBuffer.length = 0;
    window.sessionStorage.removeItem('restoreBuffer')
  }

  public selectAll() {
    this.files.forEach(file => {
      file.isSelected = true
    })
  }

  public checkFileJobUpdate(job: JobsListItem) {
    let loopIdx = -1;
    const removeIdxes = [];
    this.files.forEach(file => {
      loopIdx++;
      if (file.jobID === job.job_id) {
        if (job.status === 2) {
          file.isPending = false
          file.jobID = ''
          if (file.jobNextAction === 'delete') {
            removeIdxes.push(loopIdx)
          }
        }
        if (job.status >= 3) {
          file.isPending = false
          file.isError = true
          file.jobID = ''
        }
      }
    })
    for (let i = removeIdxes.length - 1; i >= 0; i--) {
      this.files.splice(removeIdxes[i], 1);
      this.files = [...this.files]; // force listing refresh
    }
  }
  public addFiles(files: File[]) {
    this._files = this._files.concat(files);
    this.sortFiles(this.savedSortRule);
  }
  public addFile(file: File) {
    this._files.push(file);
    this.sortFiles(this.savedSortRule);
  }
  public addListItem(listItem: ListItem) {
    this._listItems.push(listItem);
    this.sortItems(this.savedSortRule);
  }

  constructor(
  ) {
    this.savedSortRule = localStorage.getItem('sortOrder') || 'date_desc';
    this.files = []
    const copyBuffer = JSON.parse(window.sessionStorage.getItem('copyBuffer'));
    // Set used to avoid duplications
    if (copyBuffer) {
      this.copyBuffer = Array.from(new Set(copyBuffer))
    }
    const moveBuffer = JSON.parse(window.sessionStorage.getItem('moveBuffer'));
    if (moveBuffer) {
      this.moveBuffer = Array.from(new Set(moveBuffer))
    }
    const restoreBuffer = JSON.parse(window.sessionStorage.getItem('restoreBuffer'));
    if (restoreBuffer) {
      this.restoreBuffer = Array.from(new Set(restoreBuffer))
    }
  }

  public sortFiles(key = 'name_asc'): void {
    this.savedSortRule = key
    const { field, direction } = ListingModel.SORT_DIRECTIONS[key.toLowerCase()]
    this._files.sort((a: File, b: File) => {
      if (a.type === 's' && (b.type === 'd' || b.type === 'f')) {
        return  -1;
      } // shared folders at top
      if (b.type === 's' && (a.type === 'd' || a.type === 'f')) {
        return 1;
      } // shared folders at top
      if (a.type === 'd' && b.type === 'f') {
        return  -1;
      } // folders at top
      if (b.type === 'd' && a.type === 'f') {
        return 1;
      } // folders at top
      if (field === 'name') {
        return a.name.toLowerCase() > b.name.toLowerCase() ? direction : a.name.toLowerCase() < b.name.toLowerCase() ? direction * -1 : 0;
      }
      return a[field] > b[field] ? direction : a[field] < b[field] ? direction * -1 : 0;
    })
    this._files = [...this._files]; // Tels virtual scroll to re-render
  }

  public sortItems(key = 'name_asc'): void {
    this.savedSortRule = key
    const { field, direction } = ListingModel.SORT_DIRECTIONS[key.toLowerCase()]
    this._listItems.sort((a: ListItem, b: ListItem) => {
      if (field === 'name') {
        return a.name.toLowerCase() > b.name.toLowerCase() ? direction : a.name.toLowerCase() < b.name.toLowerCase() ? direction * -1 : 0;
      }
      if (field === 'points' && a.itemFullData) {
        return a.itemFullData['points'] > b.itemFullData['points'] ? direction : a.itemFullData['points'] < b.itemFullData['points'] ? direction * -1 : 0;
      }
      if (field === 'id' && a.itemFullData) {
        return parseInt(a.itemFullData['id']) > parseInt(b.itemFullData['id']) ? direction : parseInt(a.itemFullData['id']) < parseInt(b.itemFullData['id']) ? direction * -1 : 0;
      }
      return a[field] > b[field] ? direction : a[field] < b[field] ? direction * -1 : 0;
    })
    this._listItems = [...this._listItems]; // Tels virtual scroll to re-render
  }
}
